import { IONote } from '../io/io-note.class';
import { isValidEmailAddress } from '../../utility/common.utility';

// `indskr_samplerequest.indskr_type` optionset value that marks a record as a
// Sample Delivery Request (vs. other sample-request types sharing the entity).
// NOTE: this is the *type* field — it is unrelated to `statuscode` even though
// it happens to share the numeric value of `SdrStatus.Submitted`.
export const SDR_TYPE_DELIVERY_REQUEST = 268890001;

// SDR status optionset (`indskr_samplerequest.statuscode`). Values confirmed
// from Dynamics. The optionset may grow server-side over time — anything not
// listed here is still rendered using the raw `statuscode` FormattedValue
// (`statusLabel`) and falls back to the default (blue) pill colour, so a new
// status never breaks the UI; it just won't get a bespoke colour until added.
export enum SdrStatus {
  Active = 1,
  Inactive = 2,
  Submitted = 268890001,
  SentTo3PL = 268890002,
  Delivered = 268890003,
  Signed = 268890004,
  PendingSignature = 268890005,
  Expired = 268890007
}

/**
 * Status pill colour token for a given `statuscode`. Maps as follows:
 * - Delivered: green (final delivered state)
 * - Active: grey (Draft state — SDR being edited inline, not yet finalized)
 * - All other states: blue (submitted but not yet delivered, or unmapped/unknown)
 * Pair with `statusLabel` (the FormattedValue) for the displayed text.
 */
export function getSdrStatusColor(statusCode: number): 'green' | 'blue' | 'grey' {
  if (statusCode === SdrStatus.Delivered) return 'green';
  // Active = the SDR is a Draft, still being edited inline (create-on-New,
  // not yet finalized) — grey so its tab pill reads distinctly from a
  // submitted-but-not-yet-delivered record, which is also otherwise blue.
  // Note: isNotifiableSdrStatus treats Active as non-notifiable (housekeeping),
  // a different concern from this UI display mapping.
  if (statusCode === SdrStatus.Active) return 'grey';
  return 'blue';
}

/**
 * 2-color variant used ONLY by the inline meeting-embedded tab pill —
 * matches Sample Drop's own tab-dot exactly (blue = still open/in-progress,
 * green = done from the rep's perspective), rather than getSdrStatusColor's
 * 3-color scheme (grey/blue/green), which the standalone detail view's
 * status badge still uses and must not change. Active (Draft, not yet
 * submitted) = blue ("still open", same as Drop's not-yet-completed
 * allocation); Submitted or later (Submitted/SentTo3PL/Delivered/Signed) =
 * green ("done" from the rep's perspective — fulfillment progress past that
 * point is tracked elsewhere, not by this pill).
 */
export function getSdrInlinePillColor(statusCode: number): 'green' | 'blue' {
  return statusCode === SdrStatus.Active ? 'blue' : 'green';
}

/**
 * Display title for an SDR. The autonumber `indskr_name` is assigned server-side,
 * so it is empty on every record that has not synced yet, in which case the raw
 * `title` is `''`. Falls back to the caller-supplied generic label so detail
 * headers and timeline cards never render a blank / dangling title.
 * Translate-agnostic on purpose — the caller passes the already-translated
 * `SAMPLE_DELIVERY_REQUEST` label, keeping i18n out of the data layer.
 *
 * `contactName` disambiguates two untitled SDRs from each other. NOT the record
 * id: an unsynced SDR's id is a client-generated GUID, which rendered as
 * "Sample Delivery Request 3021eb45-4c18-…" (OMNI-61543).
 *
 * Kept byte-identical to the OMP release line's own OMNI-61543 fix (commit
 * 4548e879f9), including the " - " separator — the two branches cherry-pick
 * between each other, so any gratuitous difference here becomes a merge
 * conflict and a title that differs between releases.
 */
export function sdrDisplayTitle(rawTitle: string, fallback: string, contactName?: string): string {
  if (rawTitle && rawTitle.trim()) return rawTitle;
  const name = contactName?.trim();
  return name ? `${fallback} - ${name}` : fallback;
}

/**
 * Removes the single dangling separator the backend leaves on an SDR title when
 * its secondary-info config has an empty suffix (server appends `separator` after
 * the last attribute regardless). Anchored to the END only — the separator (e.g.
 * "-") also appears inside values like "SDR-00001", so a global replace is wrong.
 * No-op unless the suffix is unconfigured and the (right-trimmed) title actually
 * ends with the separator.
 */
export function stripSdrTitleTrailingSeparator(
  title: string,
  separator: string,
  suffixConfigured: boolean,
): string {
  if (!title || suffixConfigured) return title;
  const sep = (separator ?? '').trim();
  if (!sep) return title;
  const trimmed = title.replace(/\s+$/, '');
  if (!trimmed.endsWith(sep)) return title;
  return trimmed.slice(0, trimmed.length - sep.length).replace(/\s+$/, '');
}

/**
 * Email format check for the free-text alternate-email field. Delegates to the
 * shared app-wide validator (the stricter pattern used by consent capture and
 * email-activity detail) so SDR enforces the same bar — rejecting 1-char TLDs,
 * malformed domains and disallowed local-part characters. Kept as a thin
 * re-export so existing SDR call sites and specs are unaffected.
 */
export function isValidEmail(value: string): boolean {
  return isValidEmailAddress(value);
}

/**
 * The status text to SHOW for an SDR. A record that has not reached Dynamics yet
 * reports its SYNC state rather than its optionset label: offline write paths
 * stamp `statuscode@…FormattedValue` as "Submitted", which is true of the local
 * record but misleading about the server (OMNI-61603/OMNI-61597).
 *
 * Every surface that displays OR filters on SDR status must go through this, or
 * the timeline card and its status filter drift apart — a card reading
 * "Pending Sync" filed under a "Submitted" filter option. Translate-agnostic on
 * purpose, same convention as `sdrDisplayTitle`: the caller passes the
 * already-translated PENDING_SYNC label.
 */
export function sdrDisplayStatus(
  sdr: { pendingSync?: boolean; statusLabel?: string },
  pendingSyncLabel: string,
): string {
  if (sdr?.pendingSync) return pendingSyncLabel;
  return sdr?.statusLabel ?? '';
}

/** Notification type string for SDR status changes. Kept here (not only on the
 *  NOTIFICATION enum) so the pure builder + its tests have no service import. */
export const SDR_STATUS_CHANGED_NOTIFICATION_TYPE = 'SAMPLE_DELIVERY_REQUEST_STATUS_CHANGED';

/**
 * Whether this status should raise a My Assistant notification for the rep.
 * Submitted is the initial creation state (the rep raised it themselves) and
 * Active/Inactive are housekeeping state-code flips — none of those are worth
 * notifying. Every other status (current and future, e.g. SentTo3PL, Delivered,
 * Signed) is a server-side progression the rep should know about.
 */
export function isNotifiableSdrStatus(statusCode: number): boolean {
  return (
    statusCode !== SdrStatus.Submitted &&
    statusCode !== SdrStatus.Active &&
    statusCode !== SdrStatus.Inactive
  );
}

/**
 * Whether this SDR should appear in the Contact Timeline (OMNI-61542). Active
 * is the Auto Save/Draft state — not yet submitted by the rep — so it's kept
 * out of the timeline; Submitted and every later status are shown.
 */
export function isSdrVisibleInTimeline(statusCode: number): boolean {
  return statusCode !== SdrStatus.Active;
}

/** Minimal notification model shape (subset of IndNotificationDataModel) the
 *  pure builder returns — the caller adds icon and fans it into the service. */
export interface SdrStatusNotificationModel {
  type: string;
  id: string;
  name?: string;
  DateTime: number;
  data: SampleDeliveryRequest;
  isRed: boolean;
  params: { requestId: string; hcpName: string; newStatus: string };
}

/**
 * Build an in-app notification model for an SDR status change, or null when the
 * status isn't notifiable. The id encodes the statusCode so each transition
 * dedupes once (createCustomNotification dedupes by id) while still firing for
 * every distinct status (AC1). `name` is left undefined — the caller fills it
 * via translate(type, params) so i18n stays out of the data layer.
 */
export function buildSdrStatusNotificationModel(
  sdr: SampleDeliveryRequest,
): SdrStatusNotificationModel | null {
  if (!sdr?.id || !isNotifiableSdrStatus(sdr.statusCode)) return null;
  return {
    type: SDR_STATUS_CHANGED_NOTIFICATION_TYPE,
    id: `${SDR_STATUS_CHANGED_NOTIFICATION_TYPE}_${sdr.id}_${sdr.statusCode}`,
    DateTime: sdr.createdOn ? sdr.createdOn.getTime() : 0,
    data: sdr,
    isRed: false,
    params: {
      requestId: sdr.title || sdr.id,
      hcpName: sdr.contactName || '',
      newStatus: sdr.statusLabel || '',
    },
  };
}

// Carries parent brand alongside SKU — the brand→SKU hierarchy is otherwise lost when the picker flattens caseProductSKU.
export interface SdrPickerSku {
  skuId: string;
  skuName: string;
  brandId: string;
  brandName: string;
}

export interface SdrProductLine {
  skuId: string;
  skuName: string;
  productId?: string;  // parent brand; absent on pre-fix records
  productName?: string;
  /** The real Dynamics line-item id (indskr_samplereconciliationid) for an
   *  already-persisted product line — undefined for a line that hasn't been
   *  saved yet (e.g. still in-flight, or added purely offline with no
   *  server-assigned id). Populated by groupSdrRows when hydrating an
   *  existing SDR from a fetch, and by the inline component's own
   *  save-on-click tracking (productLineIds) for a freshly-added line. */
  lineId?: string;
  qty: number;
  minQty: number;
  maxQty: number | null; // null = no configured cap
  hasLimit?: boolean;    // true = a BU iO config entry exists for this SKU
  /**
   * Customer's remaining Manual-Distribution allocation balance for this SKU,
   * resolved from SampleDeliveryRequestService.getAllocationForSku when the
   * line was added. Undefined/null when no Customer Allocation record was
   * found (or the sync hasn't completed/failed/is offline) — this is a
   * best-effort client-side pre-check only, Dynamics remains the
   * authoritative validator at submit time. Note: null here does NOT by
   * itself mean the line is safe to submit — see allocationNotEligible,
   * which can be true even when this is null (confirmed: Dynamics rejects a
   * SKU with zero matching configs outright, "No Allocation Config found.").
   */
  allocationRemaining?: number | null;
  /** True when the matched Customer Allocation record is unlimited — remaining is ignored (never blocks) when set. */
  allocationUnlimited?: boolean;
  /**
   * True when GoApp has positively confirmed this line will be rejected by
   * Dynamics — mirrors one of: "Customer is not eligible for Sample Request
   * for this SKU" (config exists, Allocation↔Contact N:N not associated, no
   * prior Customer Allocation record either), an expired/not-yet-started
   * config window, or "No Allocation Config found." (zero Manual-Distribution
   * configs at all for this SKU — confirmed live, Dynamics does NOT allow an
   * untracked SKU through). That last case only sets true once GoApp's own
   * allocation cache for this contact has actually been populated (live or
   * disk-fallback) — if sync hasn't run/failed/offline-with-nothing-cached,
   * this stays false rather than blocking on data we don't actually have —
   * see SampleDeliveryRequestService.isAllocationBlocking.
   */
  allocationNotEligible?: boolean;
  /**
   * The config's own ceiling and the window it is valid for, shown beside the
   * quantity (OMNI-62147). Display-only — none of them gate the line
   * (allocationRemaining/allocationNotEligible do that), and all are null
   * whenever no allocation was matched.
   */
  allocationEligible?: number | null;
  allocationStart?: Date | null;
  allocationEnd?: Date | null;
}

/** What `_resolveAllocationDisplay` hands a product line — the gating pair
 *  plus OMNI-60585's four display figures, resolved in one place so the two
 *  SDR forms cannot drift. */
export interface SdrAllocationDisplay {
  remaining: number | null;
  unlimited: boolean;
  notEligible: boolean;
  eligible: number | null;
  start: Date | null;
  end: Date | null;
}

export interface SdrIoConfigLimit {
  id: string;
  min: number;
  max: number;
}

/**
 * A Manual-Distribution Allocation Config (indskr_sampleproductdropconfiguration)
 * for one SKU, LEFT OUTER joined to the contact's Customer Allocation record
 * (indskr_customersampleproduct) if one already exists — the eligibility
 * ceiling the SDR limit-validation feature reads for its client-side
 * pre-submission check. Rooted at the config (not Customer Allocation) so a
 * row still comes back even on a customer's first-ever request for that SKU,
 * when no Customer Allocation record exists yet. See OMNI-59706 and the
 * fetch's own comment (fetchQueries.sampleDeliveryRequest.
 * fetchManualAllocationsForContact) for the full design.
 *
 * `id` is undefined exactly when no Customer Allocation record exists yet —
 * in that case `remaining` falls back to the config's own untouched `limit`
 * (nothing consumed yet), rather than being unavailable. Once a Customer
 * Allocation record exists, `remaining` is its own (already net-of-
 * consumption, confirmed) indskr_totalsamplesremaining.
 *
 * NOTE: best-effort pre-check only, not authoritative — Dynamics re-validates
 * (and is the only thing that updates remaining) at actual submit time.
 * Confirmed design: remaining is a flat limit valid across the whole
 * [startDate, endDate] window with no interim reset — indskr_periodduration /
 * indskr_perioddurationunit are NOT part of this feature's model.
 */
export interface SdrCustomerAllocation {
  /** Customer Allocation record id — undefined when none exists yet (first
   *  request for this customer+SKU; remaining falls back to `limit`). */
  id?: string;
  skuId: string;
  /** The Allocation Config's own configured limit — always present, even
   *  when no Customer Allocation record exists yet. */
  limit: number;
  /** The ceiling to validate requested quantity against: the Customer
   *  Allocation's own remaining balance when one exists, else `limit`. */
  remaining: number;
  dropped: number;
  isUnlimited: boolean;
  startDate: Date | null;
  endDate: Date | null;
  allocationConfigId: string;
  /**
   * Whether this contact is associated with the Allocation Config via the
   * Allocation↔Contact N:N (the "aa" outer join) — true when associated,
   * false when the config exists but this specific contact isn't on it. Only
   * meaningful when `id` is undefined (no Customer Allocation record yet):
   * once a Customer Allocation record exists, Dynamics' own plugin no longer
   * re-checks this association (ProcessCustomerAllocation only checks it in
   * the no-existing-record branch), so GoApp shouldn't block on it either —
   * see SampleDeliveryRequestService.getAllocationForSku.
   */
  isEligible: boolean;
}

/**
 * The meeting an SDR is being raised from, when there is one. Only the
 * in-meeting flow supplies it; the standalone form has no meeting and passes
 * nothing, which disables the rule — see isAllocationBlocking (OMNI-61513).
 */
export interface SdrMeetingWindow {
  start?: Date | null;
  end?: Date | null;
}

export class SampleDeliveryRequest {
  id: string;
  title: string;
  /** indskr_sdrnumber — a dedicated SDR business number, distinct from
   *  indskr_name (title, Dynamics' generic autonumber). Currently only
   *  consumed by the inline meeting-embedded tab pill's label. */
  sdrNumber: string;
  contactId: string;
  contactName: string;
  deliveryAddress: string;
  deliveryAddressName: string;
  email: string;
  emailAddress: string;
  /** Free-text alternate delivery address (confirmed schema indskr_AlternativeAddress).
   *  Mutually exclusive with the address lookup — exactly one side is set per SDR. */
  alternateAddress: string;
  /** Free-text alternate email (confirmed schema indskr_AlternativeEmailAddress). */
  alternateEmail: string;
  products: SdrProductLine[];
  notes: IONote[];
  statusCode: number;
  statusLabel: string;
  createdOn: Date;
  /**
   * Meeting this SDR was raised from (OMNI-61002). Parsed from the appointment
   * lookup `_indskr_appointment_value`; '' for SDRs created outside a meeting.
   * Used to scope the per-meeting SDR list (Request tab) — for both offline
   * pending SDRs (filtered from the local cache) and server rows once the
   * OMNI-61003 appointment lookup lands.
   */
  appointmentId: string;
  /**
   * Whether this instance carries full detail (notes/attachments), as opposed to
   * a slim timeline/`allSdrs` summary. This is the detail-hydration discriminator
   * the detail view uses to decide whether to fetch/read the full record — it
   * replaces the old `!contactId` heuristic, which broke once `allSdrs`,
   * notification, and warm-create objects all started carrying `contactId`
   * (OMNI-57234). `true` only on the `fetchSdrById` and `createSdr` paths.
   */
  notesLoaded: boolean;
  /**
   * True only for an offline-created SDR still waiting on `uploadPendingSdrs`
   * to replay it. `statusCode` is already stamped `Submitted` at offline-create
   * time (see createSdrOffline), so this is the only signal the UI has that the
   * record hasn't actually reached Dynamics yet. A server-fetched or
   * successfully-synced instance never carries `raw['pendingSync']`, so this
   * defaults to `false` for those.
   */
  pendingSync: boolean;

  constructor(raw: Record<string, any>) {
    this.id = raw['indskr_samplerequestid'];
    this.title = raw['indskr_name'] ?? '';
    this.sdrNumber = raw['indskr_sdrnumber'] ?? '';
    this.contactId = raw['_indskr_customer_value'] ?? '';
    this.contactName =
      raw['_indskr_customer_value@OData.Community.Display.V1.FormattedValue'] ?? '';
    this.deliveryAddress = raw['_indskr_address_value'] ?? '';
    this.deliveryAddressName =
      raw['_indskr_address_value@OData.Community.Display.V1.FormattedValue'] ?? '';
    this.email = raw['_indskr_emailaddress_value'] ?? '';
    this.emailAddress =
      raw['_indskr_emailaddress_value@OData.Community.Display.V1.FormattedValue'] ?? '';
    // Free-text alternate delivery address / email. Default '' so the display
    // rule (alternate || saved-lookup) can treat them as a truthiness check.
    this.alternateAddress = raw['indskr_alternativeaddress'] ?? '';
    this.alternateEmail = raw['indskr_alternativeemailaddress'] ?? '';
    this.statusCode = raw['statuscode'];
    this.statusLabel =
      raw['statuscode@OData.Community.Display.V1.FormattedValue'] ?? '';
    // Appointment lookup (OMNI-61002/OMNI-61003). Offline-created SDRs stamp this
    // directly; server rows expose it as `_indskr_appointment_value` once the
    // OMNI-61003 lookup ships.
    this.appointmentId = raw['_indskr_appointment_value'] ?? '';
    // createdon is a system-maintained field, always present on fetched/created records.
    this.createdOn = new Date(raw['createdon']);
    this.products = [];
    this.notes = [];
    // A raw Dynamics row is a summary unless a detail/create path marks it hydrated.
    this.notesLoaded = false;
    this.pendingSync = raw['pendingSync'] === true;
  }

  /**
   * Rebuild an instance from a previously-cached (serialized) object — NOT a
   * Dynamics raw record. Restores the createdOn Date (null-safe, so a missing
   * value does not become the Unix epoch and corrupt timeline sort), products,
   * and notes. Used by the offline SDR cache (SDR_RECORD).
   */
  static fromCached(obj: any): SampleDeliveryRequest {
    // A still-offline-pending doc (createSdrDraftOffline / addSdrLineOffline /
    // finalizeSdr's offline branch) is NEVER rewritten into this cached
    // INSTANCE shape — it stays in raw Dynamics shape at the top level
    // (indskr_samplerequestid/statuscode/_indskr_customer_value/...), which
    // the ordinary constructor already parses correctly, INCLUDING id,
    // statusCode, and appointmentId. `products` and `notes` are tracked ONLY in
    // pendingFormPayload — the *Offline autosave siblings mutate those arrays
    // but never mirror them onto matching top-level fields. Route through the
    // raw constructor and overlay them (see overlayPendingPayload), instead of
    // assuming every cached doc is already-synced INSTANCE
    // shape below: that assumption silently produced id:'' (this method's
    // own fallback), statusCode: undefined, appointmentId:'', products: []
    // for any still-pending doc — losing it from its meeting's Request tab
    // entirely (wrong id/appointmentId) and rendering it with none of its
    // entered products whenever it resurfaces (app restart via
    // loadAllSdrRecordsFromDb, or resuming an offline Draft via
    // getCachedSdrRecord) (OMNI-61497 follow-up).
    // Gated on `indskr_samplerequestid`, not on the payload alone: a doc written
    // in INSTANCE shape by saveSdrRecordToDb and only later given a
    // pendingFormPayload by the *Offline siblings has no raw Dynamics keys, so
    // the raw constructor produced id:''. It falls through to the instance
    // branch below, which overlays the same payload.
    if (obj?.pendingFormPayload && obj?.indskr_samplerequestid) {
      const sdr = new SampleDeliveryRequest(obj);
      SampleDeliveryRequest.overlayPendingPayload(sdr, obj, obj.pendingFormPayload);
      return sdr;
    }
    const sdr = new SampleDeliveryRequest({});
    sdr.id = obj?.id ?? '';
    sdr.title = obj?.title ?? '';
    sdr.sdrNumber = obj?.sdrNumber ?? '';
    sdr.contactId = obj?.contactId ?? '';
    sdr.contactName = obj?.contactName ?? '';
    sdr.deliveryAddress = obj?.deliveryAddress ?? '';
    sdr.deliveryAddressName = obj?.deliveryAddressName ?? '';
    sdr.email = obj?.email ?? '';
    sdr.emailAddress = obj?.emailAddress ?? '';
    // Restore the free-text alternate fields so the value survives the offline
    // cache (SDR_RECORD) and notification `data` round-trips.
    sdr.alternateAddress = obj?.alternateAddress ?? '';
    sdr.alternateEmail = obj?.alternateEmail ?? '';
    sdr.statusCode = obj?.statusCode;
    sdr.statusLabel = obj?.statusLabel ?? '';
    sdr.appointmentId = obj?.appointmentId ?? '';
    sdr.createdOn = obj?.createdOn ? new Date(obj.createdOn) : null;
    sdr.products = Array.isArray(obj?.products) ? obj.products.map((p: any) => ({ ...p })) : [];
    sdr.notes = Array.isArray(obj?.notes) ? obj.notes.map((n: any) => new IONote(n)) : [];
    // Persist the hydration flag across the cache round-trip — a cached SDR_RECORD
    // that was hydrated when written stays hydrated on a re-read.
    sdr.notesLoaded = !!obj?.notesLoaded;
    sdr.pendingSync = !!obj?.pendingSync;
    if (obj?.pendingFormPayload) {
      SampleDeliveryRequest.overlayPendingPayload(sdr, obj, obj.pendingFormPayload);
    }
    return sdr;
  }

  /**
   * Graft an offline doc's `pendingFormPayload` onto the instance rebuilt from
   * that doc. The payload only ever holds items still awaiting their FIRST
   * create (see PendingSdrPayload), so its products/notes are APPENDED to
   * whatever the record already carries from its last online sync rather than
   * replacing them.
   *
   * Notes were previously dropped here entirely — only products were overlaid —
   * so an inline Draft's offline notes/attachments vanished from the form the
   * moment the meeting was reopened and _hydrateResumingSdr re-read the record
   * from disk (OMNI-61661).
   *
   * Offline-staged notes are flagged `pendingPushForDynamics` and carry their
   * `clientId` as the note id: they have no annotation id yet, so a consumer
   * that mistook one for a persisted note would target a non-existent Dynamics
   * record on a later edit/remove. See InlineSampleDeliveryRequestComponent's
   * _buildSeededNotes, which branches on exactly that flag.
   */
  private static overlayPendingPayload(sdr: SampleDeliveryRequest, doc: any, payload: any): void {
    // NOTE: deliberately does NOT reconcile `doc.statuscode` onto `statusCode`.
    // For a raw-shaped doc the constructor already read it, so it would be a
    // no-op; for an INSTANCE-shaped doc it is actively unsafe, because
    // _mutateSdrOfflineDoc backfills `raw.statuscode ?? Active` — so any
    // Submitted/Delivered record whose doc acquires a pendingFormPayload would
    // be silently downgraded to Active on the next disk read. The gap that
    // motivated it (an offline finalize on an instance-shaped doc leaving
    // statusCode stale) is unconfirmed; this downgrade would be a real
    // regression. Left alone until the instance-shape case is reproduced.

    const products = Array.isArray(payload?.products) ? payload.products.map((p: any) => ({ ...p })) : [];
    sdr.products = [...(sdr.products ?? []), ...products];

    const notes = Array.isArray(payload?.notes) ? payload.notes : [];
    sdr.notes = [
      ...(sdr.notes ?? []),
      ...notes.map((note: any, idx: number) =>
        new IONote({
          annotationid: note?.existingAnnotationId || note?.clientId || `offline-note-${sdr.id}-${idx}`,
          notetext: note?.noteText ?? '',
          filename: note?.attachment?.fileName ?? '',
          mimetype: note?.attachment?.mimeType ?? '',
          filesize: note?.attachment?.fileSize ?? 0,
          isdocument: !!note?.attachment,
          // Offline there is no server to re-fetch the body from, so carrying it
          // through is what makes a resumed attachment more than a filename.
          documentbody: note?.attachment?.documentBody ?? '',
          pendingPushForDynamics: !note?.existingAnnotationId,
        }),
      ),
    ];

    // Upgrade path for drafts created before createSdrDraftOffline stamped the
    // customer FormattedValue: without a name the Customer field reads "No data"
    // on reopen, and Submit rebuilds its read-only summary from that blank.
    if (!sdr.contactName && payload?.contactName) sdr.contactName = payload.contactName;
  }

  /**
   * The address to SHOW — a free-text alternate address (no lookup, so
   * `deliveryAddressName` is empty) takes precedence; otherwise the saved
   * address lookup's display name. Encodes the display rule once for callers
   * that hold a real instance (e.g. combineSdrsAndActivities). NOTE: surfaces
   * that receive a spread copy or a PouchDB-deserialized plain object (the
   * detail view, timeline activities) must read the own-props directly —
   * prototype getters do not survive a spread / JSON round-trip.
   */
  get displayDeliveryAddress(): string {
    return this.alternateAddress || this.deliveryAddressName || '';
  }

  /** The email to SHOW — alternate free-text email when present, else the saved lookup. */
  get displayEmailAddress(): string {
    return this.alternateEmail || this.emailAddress || '';
  }
}
